// Rich per-platform post preview — extracted from ReviewPublish.jsx so it can
// be reused anywhere else a post needs the same mockup (e.g. the Awaiting
// Publish view), instead of each place reimplementing its own preview.
const MK = {
  avatar: { width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #1B2B4B, #2d4a7a)', color: '#C8963E', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, flexShrink: 0 },
  name: { fontSize: 14, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 },
  sub: { fontSize: 12, color: '#64748b' },
  body: { fontSize: 14, lineHeight: 1.55, color: '#0f172a', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
};

export function stripMarkdown(text) {
  return String(text || '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/^[-*]\s+/gm, '• ');
}

export default function PlatformMockup({ platform, caption: rawCaption, mediaUrl, isVideoUrl }) {
  const caption = stripMarkdown(rawCaption);
  const p = (platform || '').toLowerCase();
  const isVertVideo = ['tiktok', 'youtube shorts', 'facebook reels', 'instagram reels'].includes(p);
  const isMicro = ['bluesky', 'threads', 'twitter', 'x / twitter', 'mastodon'].includes(p);
  const isIG = p === 'instagram';
  const brand = {
    'linkedin': { name: 'LinkedIn', accent: '#0A66C2' },
    'facebook': { name: 'Facebook', accent: '#1877F2' },
    'mastodon': { name: 'Mastodon', accent: '#6364FF' },
    'bluesky': { name: 'Bluesky', accent: '#0085FF' },
    'threads': { name: 'Threads', accent: '#000000' },
    'twitter': { name: 'X', accent: '#000000' },
    'x / twitter': { name: 'X', accent: '#000000' },
    'instagram': { name: 'Instagram', accent: '#E1306C' },
    'tiktok': { name: 'TikTok', accent: '#010101' },
    'youtube shorts': { name: 'YouTube Shorts', accent: '#FF0000' },
    'facebook reels': { name: 'Facebook Reels', accent: '#1877F2' },
    'instagram reels': { name: 'Instagram Reels', accent: '#E1306C' },
  }[p] || { name: platform || 'Post', accent: '#1B2B4B' };

  const Header = ({ compact }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: compact ? 6 : 10 }}>
      <div style={{ ...MK.avatar, width: compact ? 32 : 40, height: compact ? 32 : 40, fontSize: compact ? 13 : 16 }}>GS</div>
      <div>
        <div style={MK.name}>Gopu Shrestha</div>
        <div style={MK.sub}>Strategic Honesty · <span style={{ color: brand.accent, fontWeight: 600 }}>{brand.name}</span></div>
      </div>
    </div>
  );

  // Vertical video — phone frame with caption overlay
  if (isVertVideo) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
        <div style={{ width: 280, aspectRatio: '9/16', borderRadius: 18, overflow: 'hidden', position: 'relative', background: '#000', border: '3px solid #1B2B4B', flexShrink: 0 }}>
          {mediaUrl && isVideoUrl
            ? <video src={mediaUrl} controls playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: 12 }}>Video pending…</div>}
          <div style={{ position: 'absolute', left: 10, right: 56, bottom: 12, pointerEvents: 'none' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,.8)', marginBottom: 3 }}>@strategichonesty</div>
            <div style={{ fontSize: 11, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,.8)', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{caption}</div>
          </div>
          <div style={{ position: 'absolute', top: 8, left: 10, fontSize: 10, fontWeight: 800, color: '#fff', background: brand.accent, borderRadius: 5, padding: '2px 8px' }}>{brand.name}</div>
        </div>
      </div>
    );
  }

  // Instagram — square image card
  if (isIG) {
    return (
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', marginBottom: 16, background: '#fff', maxWidth: 400, margin: '0 auto 16px' }}>
        <div style={{ padding: '10px 12px' }}><Header compact /></div>
        <div style={{ aspectRatio: '1/1', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {mediaUrl && !isVideoUrl
            ? <img src={mediaUrl} alt="post" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            : mediaUrl && isVideoUrl
              ? <video src={mediaUrl} controls playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ color: '#94a3b8', fontSize: 12 }}>Image pending…</span>}
        </div>
        <div style={{ padding: '10px 12px', fontSize: 13, lineHeight: 1.5 }}>
          <span style={{ fontWeight: 700 }}>strategichonesty</span> <span style={{ whiteSpace: 'pre-wrap' }}>{caption}</span>
        </div>
      </div>
    );
  }

  // Micro-post — Bluesky / Threads / X / Mastodon (X threads split into bubbles)
  if (isMicro) {
    const parts = (p === 'twitter' || p === 'x / twitter') ? (caption || '').split(/\n\s*\n/).filter(Boolean) : [caption || ''];
    return (
      <div style={{ marginBottom: 16, maxWidth: 480, margin: '0 auto 16px' }}>
        {parts.map((txt, i) => (
          <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 14px', background: '#fff', marginBottom: 8, position: 'relative', marginLeft: i > 0 ? 20 : 0 }}>
            {i === 0 ? <Header compact /> : <div style={{ ...MK.sub, marginBottom: 4 }}>↳ reply {i} of {parts.length - 1}</div>}
            <div style={MK.body}>{txt}</div>
            {mediaUrl && i === 0 && (
              <div style={{ marginTop: 8, borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                {isVideoUrl
                  ? <video src={mediaUrl} controls playsInline preload="metadata" style={{ width: '100%', maxHeight: 260, objectFit: 'cover', display: 'block' }} />
                  : <img src={mediaUrl} alt="attachment" style={{ width: '100%', maxHeight: 480, objectFit: 'contain', display: 'block', background: '#000' }} />}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Text card — LinkedIn / Facebook
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', background: '#fff', marginBottom: 16, maxWidth: 520, margin: '0 auto 16px' }}>
      <Header />
      <div style={MK.body}>{caption}</div>
      {mediaUrl && (
        <div style={{ marginTop: 10, borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          {isVideoUrl
            ? <video src={mediaUrl} controls playsInline preload="metadata" style={{ width: '100%', maxHeight: 320, objectFit: 'cover', display: 'block' }} />
            : <img src={mediaUrl} alt="attachment" style={{ width: '100%', maxHeight: 520, objectFit: 'contain', display: 'block', background: '#000' }} />}
        </div>
      )}
    </div>
  );
}
